import {memoize} from 'lodash';
import React, {CSSProperties, FC, ReactNode, useMemo, useState} from 'react';
import {animated, useSpring} from 'react-spring';
import styled from 'styled-components';

import {
  directHoverSelector,
  hoverSelector,
} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {removeSchemaFromUrl} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useTimeout} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ExtendedError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {HttpNetworkError, HttpTimeoutError} from '../../../../libs/shared/core/src/services/http/httpError';
import {
  InterfaceEventsEnum,
  SchedulingLinksAccessPointsEnum,
  SchedulingSectionsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {
  CalendarsPanelId,
  ComposerPanelId,
  makeCalendarsPanelId,
  ViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useClipboard} from '../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  buildAvailableSlotsSummary,
  TrueAvailabilityStatusesEnum,
} from '../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {
  isSchedulingLinkAvailable,
  isSchedulingLinkOutdated,
} from '../../../helpers/models/schedulingLinkHelpers';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {DeleteSchedulingLinkDialog} from './deleteSchedulingLinkDialog';
import {SchedulingLinksListItemDropdown} from './schedulingLinksListItemDropdown';
import {outdatedSchedulingLinkCopy} from './schedulingLinkViewHelpers';
import {SchedulingLinkWarningText} from './schedulingLinkWarningText';
import {SchedulingLinkSettingsDialogContainer} from './settings/schedulingLinkSettingsDialogContainer';

/*
 * Props.
 */

export interface SchedulingLinksListItemProps {
  panelId: CalendarsPanelId | ComposerPanelId | ViewerPanelId;
  schedulingLink: SchedulingLinkModel;
  weekStart: CalendarWeekDaysEnum;
  fetchAvailabilityAndInsertMeetingTimesAsync?: () => Promise<boolean>;
  onInsertLinkClick?: () => void;
  onDeleteClick: () => void;
  isNewlyCreated: boolean;
  shouldShowInsertMeetingButtons?: boolean;
  shouldHideMoreMenu?: boolean;
  accessPoint: SchedulingLinksAccessPointsEnum;
  isQuickInsert: boolean;
}

/*
 * Constants.
 */

const animationDurationMs = 2000;
const moreIconSize = 16;
const highlightDurationMs = 3000;

/*
 * Styles.
 */

const makeNewlyCreatedItemStyle = memoize(
  (theme: UserTheme): CSSProperties => ({backgroundColor: theme.palette.blue.shade10}),
);

const makeNonNewlyCreatedItemStyle = memoize(
  (theme: UserTheme): CSSProperties => ({backgroundColor: theme.foreground}),
);

const StyledItemWrapperDiv = styled(animated.div)`
  padding: 16px;
`;

const StyledItemGrid = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 2px;
`;

const StyledTitleWrapperDiv = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto max-content;
`;

const StyledTitle = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  display: flex;
  margin-bottom: 2px;
`;

const StyledMoreButton = styled(ChromelessButton)`
  padding: 3px;
  border-radius: 4px;
  background-color: ${(p) => p.theme.alphas.transparent};
  color: ${(p) => p.theme.greys.shade60};

  ${directHoverSelector} {
    background-color: ${(p) => p.theme.alphas.gray10};
    color: ${(p) => p.theme.greys.shade70};
  }
`;

const StyledAvailabilityWrapperDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  ${ellipsis()}
`;

const StyledBookingUrlWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: minmax(auto, max-content) auto;
`;

const StyledBookingUrlSpan = styled.span`
  display: inline-block;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.interactiveText};

  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveTextHover};
  }
  ${ellipsis()}
`;

const StyledBookingUrlIcon = styled(Icon)`
  display: inline-block;
  margin-bottom: -1px;
  color: ${(p) => p.theme.interactiveText};
  fill: none;

  ${hoverSelector} {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledButtonGroupWrapperDiv = styled.div`
  margin-top: 6px;
`;

const StyledWarningButton = styled(ChromelessButton)`
  color: ${(p) => p.theme.interactiveText};
  &:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledSpinnerDiv = styled.div`
  height: 20px;
  width: 20px;
  margin: 10px auto;
`;

const StyledAvailabilityWarningWrapperDiv = styled.div`
  display: grid;
  grid-template-areas: 'icon text';
  grid-template-columns: max-content;
  grid-column-gap: 6px;
`;

const StyledAvailabilityWarningTextDiv = styled.div`
  grid-area: text;
  font-size: ${fontSizes.verySmall};
  ${ellipsis()}
`;

const StyledAvailabilityWarningIconDiv = styled.div`
  grid-area: icon;
`;

/*
 * Component.
 */

export const SchedulingLinksListItem: FC<SchedulingLinksListItemProps> = (props) => {
  const {accessPoint, schedulingLink, weekStart, fetchAvailabilityAndInsertMeetingTimesAsync, onDeleteClick} =
    props;

  const theme = useUserTheme();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [wasJustCopied, setWasJustCopied] = useState(false);
  const [fetchAvailabilityStatus, setFetchAvailabilityStatus] = useState(AsyncStatusesEnum.LOADED);
  const [trueAvailabilityStatus, setTrueAvailabilityStatus] = useState(TrueAvailabilityStatusesEnum.UNKNOWN);
  const {copy} = useClipboard();
  const [safeSetTimeout] = useTimeout();

  const [editDialogPanelId, setEditDialogPanelId] = useState<CalendarsPanelId>();
  const onEditClick = () => setEditDialogPanelId(makeCalendarsPanelId());
  const onEditDialogClose = () => setEditDialogPanelId(undefined);

  const onCopyClick = () => {
    const eventName =
      accessPoint === SchedulingLinksAccessPointsEnum.P4
        ? InterfaceEventsEnum.SCHEDULING_P4_COPY_URL
        : InterfaceEventsEnum.SCHEDULING_COMPOSER_COPY_URL;
    trackInterfaceEvent(eventName, {
      type: SchedulingSectionsEnum.SCHEDULING_LINK,
      scheduling_link_id: schedulingLink.id,
    });

    copy(schedulingLink.bookingPageUrl);

    setWasJustCopied(true);
    safeSetTimeout(() => {
      setWasJustCopied(false);
    }, animationDurationMs);
  };

  const onInsertMeetingSlotsClick = async () => {
    if (!fetchAvailabilityAndInsertMeetingTimesAsync) {
      return undefined;
    }

    try {
      setFetchAvailabilityStatus(AsyncStatusesEnum.LOADING);
      const availabilitySlotsHaveBeenInserted = await fetchAvailabilityAndInsertMeetingTimesAsync();

      // If availability slots were found, the slots have been inserted
      // into the composer and this component is now unmounted.
      if (availabilitySlotsHaveBeenInserted) {
        return undefined;
      }

      setFetchAvailabilityStatus(AsyncStatusesEnum.LOADED);
      return setTrueAvailabilityStatus(TrueAvailabilityStatusesEnum.NO_AVAILABILITY);
    } catch (error) {
      setTrueAvailabilityStatus(TrueAvailabilityStatusesEnum.NO_AVAILABILITY);
      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        return undefined;
      }

      return reportToBugsnag(new ExtendedError('Failed to fetch schedulingLink availability.', error));
    }
  };

  const availableSlotsSummary = useMemo(
    () => buildAvailableSlotsSummary(schedulingLink, weekStart),
    [schedulingLink, weekStart],
  );
  const hasAvailableMeetingSlots = useMemo(() => {
    if (trueAvailabilityStatus === TrueAvailabilityStatusesEnum.NO_AVAILABILITY) {
      return false;
    }

    return isSchedulingLinkAvailable(schedulingLink) && !isSchedulingLinkOutdated(schedulingLink);
  }, [schedulingLink, trueAvailabilityStatus]);

  // Temporarily highlight the schedulingLink item background when it's newly created.
  const [disableAnimation, setDisableAnimation] = useState(false);
  const newlyCreatedItemStyle = makeNewlyCreatedItemStyle(theme);
  const nonNewlyCreatedItemStyle = makeNonNewlyCreatedItemStyle(theme);
  const springProps = useSpring({
    from: nonNewlyCreatedItemStyle,
    to:
      props.isNewlyCreated && !disableAnimation
        ? [newlyCreatedItemStyle, nonNewlyCreatedItemStyle]
        : nonNewlyCreatedItemStyle,
    onRest: () => setDisableAnimation(true),
    config: {
      duration: highlightDurationMs,
    },
  });

  return (
    <>
      {/* If we're in a quick insert dropdown, this component is wrapped in an
      `InteractiveListItem` that needs to control background styling. */}
      <StyledItemWrapperDiv style={props.isQuickInsert ? undefined : springProps}>
        <StyledItemGrid>
          <StyledTitleWrapperDiv>
            {renderSchedulingLinkTitle(props)}
            {!props.shouldHideMoreMenu && (
              <PopoverCoordinator
                renderButton={renderMoreButton}
                renderModal={(modalProps) => (
                  <SchedulingLinksListItemDropdown
                    {...modalProps}
                    onDeleteClick={() => setIsDeleteConfirmationOpen(true)}
                    onEditClick={onEditClick}
                  />
                )}
              />
            )}
          </StyledTitleWrapperDiv>
          {maybeRenderInlineBookingUrlButton(
            props.isQuickInsert,
            schedulingLink.bookingPageUrl,
            wasJustCopied,
            onCopyClick,
          )}
          {maybeRenderAvailableSlotsSummary(availableSlotsSummary, hasAvailableMeetingSlots)}
          {maybeRenderWarning(hasAvailableMeetingSlots, onEditClick)}
          {maybeRenderInsertMeetingButtons(
            props,
            hasAvailableMeetingSlots,
            fetchAvailabilityStatus,
            onInsertMeetingSlotsClick,
          )}
        </StyledItemGrid>
      </StyledItemWrapperDiv>
      {isDeleteConfirmationOpen && (
        <DeleteSchedulingLinkDialog
          onConfirmClick={() => {
            const eventName =
              accessPoint === SchedulingLinksAccessPointsEnum.P4
                ? InterfaceEventsEnum.SCHEDULING_P4_DELETE
                : InterfaceEventsEnum.SCHEDULING_COMPOSER_DELETE;
            trackInterfaceEvent(eventName, {
              type: SchedulingSectionsEnum.SCHEDULING_LINK,
              scheduling_link_id: schedulingLink.id,
            });

            onDeleteClick();
          }}
          onRequestClose={() => setIsDeleteConfirmationOpen(false)}
        />
      )}
      {editDialogPanelId && (
        <SchedulingLinkSettingsDialogContainer
          panelId={editDialogPanelId}
          parentPanelId={props.panelId}
          onDialogClose={onEditDialogClose}
          schedulingLinkId={schedulingLink.id}
          accessPoint={accessPoint}
          onSchedulingLinkSaved={() => setTrueAvailabilityStatus(TrueAvailabilityStatusesEnum.UNKNOWN)}
        />
      )}
    </>
  );
};

function renderSchedulingLinkTitle(props: SchedulingLinksListItemProps) {
  const {schedulingLink} = props;
  const defaultName = <Trans id="schedulinglink-item-default-name">Untitled scheduling link</Trans>;
  return (
    <StyledTitle>
      {schedulingLink.name || schedulingLink.meetingProperties.summary || defaultName}
    </StyledTitle>
  );
}

function maybeRenderInlineBookingUrlButton(
  isQuickInsert: boolean,
  url: string,
  wasJustCopied: boolean,
  onCopyClick: () => void,
) {
  if (isQuickInsert) {
    return undefined;
  }

  return (
    <InlineLinkButton onClick={onCopyClick}>
      <StyledBookingUrlWrapperDiv>
        <StyledBookingUrlSpan>{removeSchemaFromUrl(url)}</StyledBookingUrlSpan>
        <span>
          &nbsp;
          <StyledBookingUrlIcon name={wasJustCopied ? 'checkmarkBoxSmall' : 'copySmall'} />
        </span>
      </StyledBookingUrlWrapperDiv>
    </InlineLinkButton>
  );
}

function maybeRenderAvailableSlotsSummary(summary: ReactNode, hasAvailableMeetingSlots: boolean) {
  if (hasAvailableMeetingSlots) {
    return <StyledAvailabilityWrapperDiv>{summary}</StyledAvailabilityWrapperDiv>;
  }

  return (
    <StyledAvailabilityWarningWrapperDiv>
      <StyledAvailabilityWarningIconDiv>
        <Icon name="warningFilledSmall" color={PaletteColorsEnum.RED} />
      </StyledAvailabilityWarningIconDiv>
      <StyledAvailabilityWarningTextDiv>{summary}</StyledAvailabilityWarningTextDiv>
    </StyledAvailabilityWarningWrapperDiv>
  );
}

function renderMoreButton(isOpen: boolean) {
  return (
    <StyledMoreButton hasHover={isOpen}>
      <Icon name="moreVertical" hasHover={isOpen} size={moreIconSize} />
    </StyledMoreButton>
  );
}

function maybeRenderWarning(hasAvailableMeetingSlots: boolean, onEditClick: () => void) {
  if (hasAvailableMeetingSlots) {
    return undefined;
  }

  return (
    <SchedulingLinkWarningText>
      <span>{outdatedSchedulingLinkCopy}</span>
      <StyledWarningButton onClick={onEditClick} hasHover>
        <Trans id="schedulinglink-outdated-addtimes">Add time options</Trans>
      </StyledWarningButton>
    </SchedulingLinkWarningText>
  );
}

function maybeRenderInsertMeetingButtons(
  props: SchedulingLinksListItemProps,
  hasAvailableMeetingSlots: boolean,
  fetchAvailabilityStatus: AsyncStatusesEnum,
  onInsertMeetingSlotsClick: () => void,
) {
  const {shouldShowInsertMeetingButtons, onInsertLinkClick} = props;
  if (!hasAvailableMeetingSlots || !shouldShowInsertMeetingButtons) {
    return undefined;
  }

  return (
    <StyledButtonGroupWrapperDiv>
      {fetchAvailabilityStatus === AsyncStatusesEnum.LOADING && (
        <StyledSpinnerDiv>
          <Loader size={VisualSizesEnum.MEDIUM} variant={LoaderVariantsEnum.BASE} isEnabled />
        </StyledSpinnerDiv>
      )}
      {fetchAvailabilityStatus === AsyncStatusesEnum.LOADED && (
        <ButtonGroup>
          <Button
            onClick={() => {
              if (!onInsertLinkClick) {
                return;
              }

              trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_COMPOSER_INSERT, {
                scheduling_link_id: props.schedulingLink.id,
                type: SchedulingSectionsEnum.SCHEDULING_LINK,
                is_quick_insert: props.isQuickInsert,
              });

              onInsertLinkClick();
            }}
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.SECONDARY}
          >
            <Trans id="schedulinglink-item-insert-link">Insert link</Trans>
          </Button>
          <Button
            onClick={onInsertMeetingSlotsClick}
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.SECONDARY}
          >
            <Trans id="schedulinglink-item-insert-slots">Insert meeting slots</Trans>
          </Button>
        </ButtonGroup>
      )}
    </StyledButtonGroupWrapperDiv>
  );
}
